// components/modals/servicehds/SubServiceModal.jsx
import React, { useState } from 'react';
import { Badge, Row, Col } from 'react-bootstrap';
import {
    FaServicestack,
    FaPlus,
    FaTimes,
    FaSave,
    FaCheck,
    FaInfoCircle,
    FaTrash,
    FaEdit,
    FaClock,
    FaMoneyBillWave
} from 'react-icons/fa';
import ServiceHDSService from '../../../services/ServiceDHSService';
import { toast } from 'react-toastify';
import '../../../assets/css/ServiceHDSPage.css';

const SubServiceModal = ({ service, isOpen, onClose, onSubServiceCreated }) => {
    const [subServices, setSubServices] = useState([
        { name: '', amount: '', frequency: 'LUNDI_A_VENDREDI' }
    ]);
    const [isSaving, setIsSaving] = useState(false);

    if (!isOpen || !service) return null;

    // Fonction pour obtenir le libellé affichable de la fréquence
    const getFrequencyLabel = (frequency) => {
        const frequencyMap = {
            'LUNDI_A_VENDREDI': 'De lundi à vendredi',
            'CHAQUE_JOUR': 'Chaque jour',
            'TROIS_JOURS_PAR_SEMAINE': '3 jours par semaine',
            'HEURE': 'Par heure',
            'SEMAINE': 'Par semaine',
            'MOIS': 'Par mois',
            'MENSUEL': 'Par mois',
            'HEBDOMADAIRE': 'Par semaine'
        };
        return frequencyMap[frequency] || frequency;
    };

    const addSubService = () => {
        setSubServices([...subServices, { name: '', amount: '', frequency: 'LUNDI_A_VENDREDI' }]);
    };

    const removeSubService = (index) => {
        if (subServices.length > 1) {
            const newSubServices = subServices.filter((_, i) => i !== index);
            setSubServices(newSubServices);
        }
    };

    const handleSubServiceChange = (index, field, value) => {
        const newSubServices = [...subServices];
        newSubServices[index][field] = value;
        setSubServices(newSubServices);
    };

    const handleSubmit = async () => {
        try {
            setIsSaving(true);

            // Validation
            for (let i = 0; i < subServices.length; i++) {
                const subService = subServices[i];
                if (!subService.name.trim()) {
                    toast.error(`Le nom du sous-service ${i + 1} est obligatoire`);
                    return;
                }
                if (!subService.amount || parseFloat(subService.amount) <= 0) {
                    toast.error(`Le montant du sous-service ${i + 1} doit être supérieur à 0`);
                    return;
                }
            }

            // Créer tous les sous-services
            const createPromises = subServices.map(subService =>
                ServiceHDSService.createSubService({
                    name: subService.name,
                    amount: parseFloat(subService.amount),
                    frequency: subService.frequency,
                    serviceHdsId: service.id
                })
            );

            await Promise.all(createPromises);
            toast.success(`${subServices.length} sous-service(s) créé(s) avec succès`);
            onSubServiceCreated();
            onClose();
            setSubServices([{ name: '', amount: '', frequency: 'LUNDI_A_VENDREDI' }]);

        } catch (error) {
            console.error('Error creating sub-services:', error);
            toast.error('Erreur lors de la création des sous-services');
        } finally {
            setIsSaving(false);
        }
    };

    // Composant d'affichage de champ
    const DisplayField = ({ label, value, icon, children }) => (
        <div className="display-field mb-3">
            <div className="field-label">
                {icon} {label}
            </div>
            <div className="field-value bg-white rounded p-2" style={{ minWidth: 0 }}>
                {children || value}
            </div>
        </div>
    );

    return (
        <>
            {/* Modal principal - Style Bottom Sheet */}
            <div
                className="subservice-modal-overlay show"
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.8)',
                    zIndex: 1050,
                    overflowY: 'auto'
                }}
                onClick={onClose}
            >
                <div
                    className="subservice-modal-container"
                    style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        borderRadius: '24px 24px 0 0',
                        maxHeight: '95vh',
                        overflowY: 'auto',
                        animation: 'slideUp 0.4s ease'
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header fixe sticky */}
                    <div className="modal-header-fixed" style={{
                        position: 'sticky',
                        top: 0,
                        left: 0,
                        right: 0,
                        zIndex: 100,
                        backgroundColor: 'white',
                        borderBottom: '1px solid #e2e8f0',
                        borderTopLeftRadius: '24px',
                        borderTopRightRadius: '24px',
                        padding: '16px 24px'
                    }}>
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <h4 className="fw-bold mb-0 text-dark">
                                    <FaServicestack className="me-2 text-success" />
                                    Ajouter des Sous-services
                                </h4>
                                <p className="text-muted small mt-1 mb-0">
                                    {service.name} - {subServices.length} sous-service(s) à créer
                                </p>
                            </div>
                            <button
                                className="btn-close"
                                onClick={onClose}
                                disabled={isSaving}
                            ></button>
                        </div>
                    </div>

                    {/* Contenu principal */}
                    <div className="p-4">
                        {/* Informations du service */}
                        <div className="bg-light rounded-3 p-3 mb-4">
                            <h5 className="fw-bold mb-3 text-primary">
                                <FaServicestack className="me-2" />
                                Service concerné
                            </h5>
                            <DisplayField
                                label="Nom du service"
                                value={service.name}
                                icon={<FaServicestack className="text-primary" />}
                            >
                                <div
                                    className="d-flex align-items-center gap-2 flex-wrap"
                                    style={{ minWidth: 0 }}
                                >
                                    <FaServicestack className="text-primary flex-shrink-0" />
                                    <span
                                        className="fw-bold"
                                        style={{ minWidth: 0, wordBreak: 'break-word' }}
                                    >
                                        {service.name}
                                    </span>
                                    {service.description && (
                                        <Badge
                                            bg="info"
                                            className="rounded-pill px-3 py-1"
                                            style={{ whiteSpace: 'normal', maxWidth: '100%' }}
                                        >
                                            <FaInfoCircle className="me-1" />
                                            {service.description}
                                        </Badge>
                                    )}
                                </div>
                            </DisplayField>
                        </div>

                        {/* Formulaire des sous-services */}
                        <div className="bg-light rounded-3 p-3 mb-4">

                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="fw-bold mb-0 text-success">
                                    <FaPlus className="me-2" />
                                    Sous-services à créer ({subServices.length})
                                </h5>
                                <button
                                    type="button"
                                    className="btn btn-success btn-sm rounded-pill px-3 d-flex align-items-center gap-2 w-auto"
                                    style={{ maxWidth: '200px' }}
                                    onClick={addSubService}
                                    disabled={isSaving}
                                >
                                    <FaPlus size={12} /> Ajouter un autre
                                </button>
                            </div>

                            {/* Liste des sous-services */}
                            <div className="sub-services-list">
                                {subServices.map((subService, index) => (
                                    <div
                                        key={index}
                                        className="bg-white rounded-3 p-3 mb-3"
                                        style={{
                                            border: '1px solid #e2e8f0',
                                            transition: 'all 0.2s ease'
                                        }}
                                    >

                                        <div className="d-flex justify-content-between align-items-start mb-3">
                                            <h6 className="fw-bold text-primary mb-0">
                                                Sous-service #{index + 1}
                                            </h6>
                                            {subServices.length > 1 && (
                                                <button
                                                    type="button"
                                                    className="btn btn-danger btn-sm rounded-pill px-3 d-flex align-items-center gap-2 flex-shrink-0"
                                                    style={{ maxWidth: '150px' }}
                                                    onClick={() => removeSubService(index)}
                                                    disabled={isSaving}
                                                >
                                                    <FaTrash size={14} /> Supprimer
                                                </button>
                                            )}
                                        </div>

                                        <Row className="g-3">
                                            <Col md={4}>
                                                <div className="form-group">
                                                    <label className="fw-bold small text-muted mb-1">
                                                        <FaServicestack className="me-1" />
                                                        Nom *
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={subService.name}
                                                        onChange={(e) => handleSubServiceChange(index, 'name', e.target.value)}
                                                        className="form-control"
                                                        placeholder="Ex: Garde d'enfants"
                                                        disabled={isSaving}
                                                        style={{
                                                            borderRadius: '8px',
                                                            border: '1px solid #e2e8f0'
                                                        }}
                                                    />
                                                </div>
                                            </Col>
                                            <Col md={3}>
                                                <div className="form-group">
                                                    <label className="fw-bold small text-muted mb-1">
                                                        <FaMoneyBillWave className="me-1" />
                                                        Montant par mois (FCFA) *
                                                    </label>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        min="0"
                                                        value={subService.amount}
                                                        onChange={(e) => handleSubServiceChange(index, 'amount', e.target.value)}
                                                        className="form-control"
                                                        placeholder="0.00"
                                                        disabled={isSaving}
                                                        style={{
                                                            borderRadius: '8px',
                                                            border: '1px solid #e2e8f0'
                                                        }}
                                                    />
                                                </div>
                                            </Col>
                                            <Col md={3}>
                                                <div className="form-group">
                                                    <label className="fw-bold small text-muted mb-1">
                                                        <FaClock className="me-1" />
                                                        Fréquence *
                                                    </label>
                                                    <select
                                                        value={subService.frequency}
                                                        onChange={(e) => handleSubServiceChange(index, 'frequency', e.target.value)}
                                                        className="form-control"
                                                        disabled={isSaving}
                                                        style={{
                                                            borderRadius: '8px',
                                                            border: '1px solid #e2e8f0'
                                                        }}
                                                    >
                                                        <option value="LUNDI_A_VENDREDI">De lundi à vendredi</option>
                                                        <option value="CHAQUE_JOUR">Chaque jour</option>
                                                        <option value="TROIS_JOURS_PAR_SEMAINE">3 jours par semaine</option>
                                                    </select>
                                                </div>
                                            </Col>
                                            <Col md={2}>
                                                <div className="form-group">
                                                    <label className="fw-bold small text-muted mb-1">
                                                        &nbsp;
                                                    </label>
                                                    <div className="mt-2">
                                                        <Badge
                                                            bg="info"
                                                            className="rounded-pill px-3 py-2 w-100 text-center"
                                                            style={{ fontSize: '0.75rem' }}
                                                        >
                                                            {getFrequencyLabel(subService.frequency)}
                                                        </Badge>
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>

                                        {/* Aperçu du sous-service */}
                                        {subService.name && subService.amount && (
                                            <div className="mt-2 pt-2 border-top">
                                                <small className="text-muted">
                                                    <FaInfoCircle className="me-1" />
                                                    Aperçu : <strong>{subService.name}</strong> -
                                                    <strong>{subService.amount} FCFA</strong> /
                                                    {getFrequencyLabel(subService.frequency)}
                                                </small>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {/* Message si aucun sous-service */}
                            {subServices.length === 0 && (
                                <div className="text-center py-4 bg-white rounded-3">
                                    <FaPlus size={48} className="text-muted mb-2" />
                                    <p className="text-muted mb-2">Aucun sous-service à créer</p>
                                    <button
                                        type="button"
                                        className="btn btn-success btn-sm rounded-pill px-3"
                                        onClick={addSubService}
                                        disabled={isSaving}
                                    >
                                        <FaPlus /> Ajouter un sous-service
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Récapitulatif */}
                        {subServices.length > 0 && (
                            <div className="bg-light rounded-3 p-3 mb-4">
                                <h5 className="fw-bold mb-3 text-secondary">
                                    <FaInfoCircle className="me-2" />
                                    Récapitulatif
                                </h5>
                                <div className="bg-white rounded-3 p-3">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <span className="text-muted">Nombre de sous-services :</span>
                                            <strong className="ms-2">{subServices.length}</strong>
                                        </div>
                                        <div>
                                            <span className="text-muted">Service :</span>
                                            <strong className="ms-2">{service.name}</strong>
                                        </div>
                                    </div>
                                    <div className="mt-2">
                                        {subServices.map((sub, idx) => (
                                            <div key={idx} className="d-flex align-items-center gap-2 mt-1">
                                                <Badge bg="success" className="rounded-pill px-2">
                                                    {idx + 1}
                                                </Badge>
                                                <span className="text-muted small">
                                                    {sub.name || 'Sans nom'} -
                                                    {sub.amount ? `${sub.amount} FCFA` : 'Montant non défini'} /
                                                    {getFrequencyLabel(sub.frequency)}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer fixe avec actions */}
                    <div className="modal-footer-fixed" style={{
                        position: 'sticky',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        borderTop: '1px solid #e2e8f0',
                        padding: '16px 24px',
                        borderBottomLeftRadius: '24px',
                        borderBottomRightRadius: '24px'
                    }}>
                        <div className="d-flex justify-content-end gap-3">
                            <button
                                className="btn btn-outline-secondary rounded-pill px-4 d-flex align-items-center gap-2"
                                onClick={onClose}
                                disabled={isSaving}
                                type="button"
                            >
                                <FaTimes /> Annuler
                            </button>
                            <button
                                className="btn btn-success rounded-pill px-4 d-flex align-items-center gap-2"
                                onClick={handleSubmit}
                                disabled={isSaving || subServices.some(s => !s.name || !s.amount)}
                                type="button"
                            >
                                {isSaving ? (
                                    <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span> Création... </>
                                ) : (
                                    <><FaSave /> Créer {subServices.length} sous-service(s)</>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Styles CSS intégrés */}
            <style jsx>{`
                @keyframes slideUp {
                    from { transform: translateY(100%); }
                    to { transform: translateY(0); }
                }

                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                .subservice-modal-overlay {
                    animation: fadeIn 0.3s ease;
                }

                .modal-header-fixed, .modal-footer-fixed {
                    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
                }

                ::-webkit-scrollbar {
                    width: 6px;
                }

                ::-webkit-scrollbar-track {
                    background: #f1f1f1;
                }

                ::-webkit-scrollbar-thumb {
                    background: #888;
                    border-radius: 3px;
                }

                ::-webkit-scrollbar-thumb:hover {
                    background: #555;
                }

                .field-label {
                    font-size: 0.85rem;
                    font-weight: 600;
                    color: #2c3e50;
                    margin-bottom: 0.5rem;
                }

                .field-value {
                    font-size: 0.95rem;
                    color: #1a202c;
                }

                .display-field .form-control {
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    padding: 8px 12px;
                }

                .display-field .form-control:focus {
                    border-color: #28a745;
                    box-shadow: 0 0 0 0.2rem rgba(40, 167, 69, 0.25);
                }

                /* Animation d'entrée pour les sous-services */
                .bg-white.rounded-3.p-3.mb-3 {
                    animation: fadeIn 0.3s ease;
                }

                /* Style des champs en focus */
                .form-control:focus {
                    border-color: #28a745;
                    box-shadow: 0 0 0 0.2rem rgba(40, 167, 69, 0.25);
                }

                /* Hover sur les cartes de sous-service */
                .bg-white.rounded-3.p-3.mb-3:hover {
                    border-color: #28a745;
                    box-shadow: 0 2px 8px rgba(40, 167, 69, 0.1);
                }

                .spinner-border {
                    width: 1rem;
                    height: 1rem;
                    border-width: 0.15em;
                }
            `}</style>
        </>
    );
};

export default SubServiceModal;